import React, { useState, useRef, useMemo, useCallback, forwardRef, useImperativeHandle } from 'react';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { Dialog } from 'primereact/dialog';

// ─── Types ────────────────────────────────────────────────────────────────────
// Mirrors the Go backend exactly: adminControllers.StudentBulkCSVController /
// BulkCreateStudentsFromCSV, mounted at POST /students/bulk-csv by
// RegisterStudentBulkCSVRoutes (superadmin, admin, principal only).

interface BulkCSVRowResult {
  status: 'success' | 'error';
  row: number;
  message: string;
  student_name?: string;
  student_id?: string;
  sp_student_id?: string;
  user_id?: string;
  class_name?: string;
  section?: string;
  roll_no?: number;
}

interface BulkCSVSummary {
  total_rows: number;
  succeeded: number;
  failed: number;
}

interface BulkCSVResponse {
  status: 'success' | 'error' | 'partial';
  message: string;
  summary: BulkCSVSummary;
  results: BulkCSVRowResult[];
}

interface ApiClient {
  post: (url: string, data: any, config?: any) => Promise<{ data: BulkCSVResponse }>;
}

interface BulkRegisterStudentsCSVProps {
  apiClient: ApiClient;
  /**
   * Required by the backend — the active academic session's ObjectID hex string.
   * Accepts null/undefined for the common case where this loads asynchronously
   * (e.g. from context or an API call) and isn't ready on first render — the
   * component just disables itself until a real value arrives.
   */
  sessionId: string | null | undefined;
  /**
   * Required by the backend — human-readable session label, e.g. "2025-2026".
   * Same null/undefined tolerance as sessionId.
   */
  session: string | null | undefined;
  /** Optional — backend falls back to context/token resolution when omitted. */
  schoolId?: string | null;
  /** Called once the upload finishes with at least one successful row. */
  onSuccess?: (summary: BulkCSVSummary) => void;
  label?: string;
  disabled?: boolean;
  /** When true the internal trigger button is not rendered (use ref.open() instead). */
  hideTrigger?: boolean;
}

export interface BulkRegisterStudentsCSVHandle {
  open: () => void;
}

type Stage = 'upload' | 'uploading' | 'results';
type ResultFilter = 'all' | 'success' | 'error';

// ─── CSV column contract ──────────────────────────────────────────────────────
// Order matches colName..colOptionalSubject (0..19) in the Go controller exactly,
// and matches the header emitted by the existing "Export ZIP" feature, so an
// exported roster can be edited and re-imported without reordering columns.

const TEMPLATE_HEADERS = [
  'name', 'gender', 'email_id', 'admission_no', 'roll_no', 'dob',
  'blood_group', 'admission_type', 'class_name', 'section',
  'father_name', 'mother_name', 'father_no', 'mother_no', 'address',
  'user_login', 'user_pass', 'date_of_admission', 'date_of_joining', 'optional_subject',
];

const REQUIRED_HEADERS = new Set([
  'name', 'gender', 'email_id', 'admission_no', 'roll_no', 'dob',
  'blood_group', 'class_name', 'father_name', 'mother_name',
  'father_no', 'mother_no', 'address', 'user_login',
]);

const TEMPLATE_EXAMPLE_ROW = [
  'Aarav Sharma', 'Male', 'aarav.sharma@example.com', 'ADM-2025-014', '14', '2014-06-21',
  'B+', 'New', 'Class 5', 'A',
  'Rakesh Sharma', 'Sunita Sharma', '9876500001', '9876500002', '221B, MG Road, Pune',
  'aarav.sharma', '', '2025-04-01', '2025-04-01', '',
];

function buildTemplateCSV(): string {
  const esc = (v: string) => `"${v.replace(/"/g, '""')}"`;
  return [
    TEMPLATE_HEADERS.map(esc).join(','),
    TEMPLATE_EXAMPLE_ROW.map(esc).join(','),
  ].join('\r\n');
}

// ─── Lightweight CSV parsing (client-side preview only — server is the source
// of truth for validation). Handles quoted fields and escaped quotes. ────────

function parseCSV(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let inQuotes = false;

  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') { field += '"'; i++; }
        else inQuotes = false;
      } else {
        field += c;
      }
    } else if (c === '"') {
      inQuotes = true;
    } else if (c === ',') {
      row.push(field); field = '';
    } else if (c === '\r') {
      // skip, \n handles the break
    } else if (c === '\n') {
      row.push(field); field = '';
      rows.push(row); row = [];
    } else {
      field += c;
    }
  }
  if (field.length > 0 || row.length > 0) { row.push(field); rows.push(row); }
  return rows.filter((r) => r.length > 1 || (r[0] ?? '').trim() !== '');
}

function downloadTextFile(filename: string, content: string, mime = 'text/csv;charset=utf-8'): void {
  const blob = new Blob(['\ufeff' + content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

// ─── Sub-components (visual language matches ExportStudentsZip) ─────────────

const SectionHeader: React.FC<{ icon: string; title: string }> = ({ icon, title }) => (
  <div style={{
    display: 'flex', alignItems: 'center', gap: 10,
    marginBottom: 16, paddingBottom: 12,
    borderBottom: '1px solid rgba(229,231,235,0.6)',
  }}>
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      width: 32, height: 32, borderRadius: 8,
      background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
      color: 'white', flexShrink: 0,
    }}>
      <i className={icon} style={{ fontSize: 15 }} />
    </div>
    <span style={{
      color: '#1e3a8a', fontWeight: 700, fontSize: 14,
      letterSpacing: '0.02em', textTransform: 'uppercase',
    }}>
      {title}
    </span>
  </div>
);

const Card: React.FC<{ children: React.ReactNode; style?: React.CSSProperties }> = ({ children, style }) => (
  <div style={{
    background: 'rgba(255,255,255,0.8)', borderRadius: 16, padding: 20,
    border: '1px solid rgba(59,130,246,0.1)',
    boxShadow: '0 4px 16px rgba(0,0,0,0.05)',
    ...style,
  }}>
    {children}
  </div>
);

// ─── Main component ───────────────────────────────────────────────────────────

const BulkRegisterStudentsCSV = forwardRef<BulkRegisterStudentsCSVHandle, BulkRegisterStudentsCSVProps>(
  (
    {
      apiClient,
      sessionId,
      session,
      schoolId,
      onSuccess,
      label = 'Bulk Register Students',
      disabled = false,
      hideTrigger = false,
    },
    ref
  ) => {
    const toast = useRef<Toast>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Both must be present before this feature can be used at all — the
    // backend rejects the request outright without session_id/session.
    const contextReady = !!sessionId && !!session;

    const [visible, setVisible] = useState(false);
    const [isClosing, setIsClosing] = useState(false);
    const [stage, setStage] = useState<Stage>('upload');
    const [dragActive, setDragActive] = useState(false);

    const [file, setFile] = useState<File | null>(null);
    const [previewRows, setPreviewRows] = useState<string[][] | null>(null);
    const [headerWarnings, setHeaderWarnings] = useState<string[]>([]);
    const [parseError, setParseError] = useState<string | null>(null);

    const [progress, setProgress] = useState(0);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const [response, setResponse] = useState<BulkCSVResponse | null>(null);
    const [resultFilter, setResultFilter] = useState<ResultFilter>('all');

    const reset = () => {
      setFile(null); setPreviewRows(null); setHeaderWarnings([]); setParseError(null);
      setProgress(0); setUploadError(null); setResponse(null); setResultFilter('all');
      setStage('upload');
    };

    const handleOpen = () => { reset(); setVisible(true); };

    useImperativeHandle(ref, () => ({
      open: handleOpen,
    }));

    const handleClose = () => {
      if (stage === 'uploading') return;
      setIsClosing(true);
      setTimeout(() => {
        setVisible(false); setIsClosing(false);
        if (response) {
          const succeeded = response.summary?.succeeded ?? 0;
          if (succeeded > 0) onSuccess?.(response.summary);
        }
        reset();
      }, 300);
    };

    const readFile = useCallback((f: File) => {
      setParseError(null);
      setHeaderWarnings([]);
      setPreviewRows(null);

      if (!f.name.toLowerCase().endsWith('.csv')) {
        setParseError('Only .csv files are accepted.');
        setFile(null);
        return;
      }

      setFile(f);
      const reader = new FileReader();
      reader.onload = () => {
        try {
          const text = String(reader.result ?? '');
          const rows = parseCSV(text);
          if (rows.length === 0) {
            setParseError('The file appears to be empty.');
            return;
          }
          const header = rows[0].map((h) => h.trim().toLowerCase());
          const missing = TEMPLATE_HEADERS.filter((h) => REQUIRED_HEADERS.has(h) && !header.includes(h));
          setHeaderWarnings(missing);
          setPreviewRows(rows);
        } catch {
          setParseError('Could not read this file as CSV.');
        }
      };
      reader.onerror = () => setParseError('Could not read this file.');
      reader.readAsText(f);
    }, []);

    const dataRowCount = previewRows ? Math.max(0, previewRows.length - 1) : 0;

    const onFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const f = e.target.files?.[0];
      if (f) readFile(f);
      e.target.value = '';
    };

    const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault(); setDragActive(false);
      const f = e.dataTransfer.files?.[0];
      if (f) readFile(f);
    };

    const handleUpload = async () => {
      if (!file || !contextReady) return;
      setStage('uploading');
      setProgress(0);
      setUploadError(null);

      const formData = new FormData();
      formData.append('session_id', sessionId as string);
      formData.append('session', session as string);
      if (schoolId) formData.append('school_id', schoolId);
      formData.append('csv_file', file);

      try {
        const res = await apiClient.post('/students/bulk-csv', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (evt: any) => {
            if (evt.total) setProgress(Math.round((evt.loaded / evt.total) * 100));
          },
        });
        setResponse(res.data);
        setStage('results');

        const { succeeded = 0, failed = 0 } = res.data.summary ?? {};
        toast.current?.show({
          severity: failed === 0 ? 'success' : succeeded === 0 ? 'error' : 'warn',
          summary: failed === 0 ? 'Import Complete' : succeeded === 0 ? 'Import Failed' : 'Import Finished With Errors',
          detail: res.data.message,
          life: 6000,
        });
      } catch (err: any) {
        const msg = err?.response?.data?.message || err?.message || 'Upload failed. Please try again.';
        setUploadError(msg);
        setStage('upload');
        toast.current?.show({ severity: 'error', summary: 'Upload Failed', detail: msg, life: 6000 });
      }
    };

    const filteredResults = useMemo(() => {
      if (!response) return [];
      if (resultFilter === 'all') return response.results;
      return response.results.filter((r) => r.status === resultFilter);
    }, [response, resultFilter]);

    const downloadErrorReport = () => {
      if (!response) return;
      const failedRows = response.results.filter((r) => r.status === 'error');
      if (failedRows.length === 0) return;
      const esc = (v: string | number | undefined) => `"${String(v ?? '').replace(/"/g, '""')}"`;
      const csv = [
        ['row', 'student_name', 'message'].map(esc).join(','),
        ...failedRows.map((r) => [r.row, r.student_name ?? '', r.message].map(esc).join(',')),
      ].join('\r\n');
      downloadTextFile(`bulk_register_errors_${new Date().toISOString().slice(0, 10)}.csv`, csv);
    };

    const canSubmit = !!file && !parseError && dataRowCount > 0 && contextReady;

    const headerTemplate = () => (
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '8px 0' }}>
        <div style={{
          width: 54, height: 54, borderRadius: 14,
          background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: 'white', fontSize: 24, fontWeight: 700,
          boxShadow: '0 8px 24px rgba(59,130,246,0.3)',
          flexShrink: 0, border: '3px solid white',
        }}>
          <i className="pi pi-users" />
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 20, fontWeight: 700, color: '#1e40af', lineHeight: 1.2, marginBottom: 4 }}>
            Bulk Register Students
          </div>
          <div style={{ fontSize: 13, color: '#64748b', fontWeight: 500 }}>
            Upload a CSV to create many students at once
          </div>
        </div>
      </div>
    );

    return (
      <>
        <Toast ref={toast} position="top-right" />

        {!hideTrigger && (
          <Button
            icon="pi pi-upload"
            label={label}
            onClick={handleOpen}
            disabled={disabled || !contextReady}
            tooltip={!contextReady ? 'Waiting for session info to load…' : undefined}
            tooltipOptions={{ position: 'top' }}
            style={{
              background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
              border: 'none', borderRadius: 10, padding: '9px 18px',
              fontSize: 13, fontWeight: 600, color: 'white', gap: 6,
              boxShadow: '0 4px 14px rgba(59,130,246,0.35)',
            }}
          />
        )}

        <Dialog
          visible={visible && !isClosing}
          onHide={handleClose}
          header={headerTemplate}
          closable={stage !== 'uploading'}
          style={{ width: '90vw', maxWidth: 640, borderRadius: 24, overflow: 'hidden' }}
          contentStyle={{
            padding: 18,
            background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
            maxHeight: 'calc(90vh - 120px)', overflowY: 'auto',
            border: '12px solid rgba(255,255,255,0.8)',
            borderRadius: '0 0 24px 24px', borderTop: 'none',
          }}
          headerStyle={{
            background: 'white', borderBottom: '1px solid #e5e7eb',
            padding: '20px 24px', borderRadius: '24px 24px 0 0',
            borderLeft: '12px solid rgba(255,255,255,0.8)',
            borderRight: '12px solid rgba(255,255,255,0.8)',
            borderTop: '12px solid rgba(255,255,255,0.8)',
            margin: '-12px -12px 0 -12px',
          }}
          modal dismissableMask={stage !== 'uploading'} draggable={false} resizable={false} blockScroll
        >
          <style>{`
            .p-dialog-mask { backdrop-filter: blur(4px); background-color: rgba(0,0,0,0.25) !important; }
            .brc-dropzone { transition: all 0.18s ease; }
            .brc-dropzone.active { border-color: #3b82f6 !important; background: rgba(59,130,246,0.06) !important; }
            .brc-cancel-btn:hover { border-color:#3b82f6 !important; color:#1e40af !important; }
            .brc-submit-btn:hover:not(:disabled) { box-shadow: 0 6px 20px rgba(59,130,246,0.45) !important; transform: translateY(-1px); }
            .brc-filter-btn { transition: all 0.15s ease; }
            .brc-result-row:hover { background: rgba(59,130,246,0.03) !important; }
          `}</style>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

            {stage === 'upload' && (
              <>
                <Card>
                  <SectionHeader icon="pi pi-file-import" title="CSV Template" />
                  <div style={{ fontSize: 13, color: '#4b5563', fontWeight: 500, marginBottom: 12, lineHeight: 1.6 }}>
                    Columns must be in this exact order: <code style={{ fontFamily: 'monospace', fontSize: 11.5 }}>
                      name, gender, email_id, admission_no, roll_no, dob, blood_group, admission_type,
                      class_name, section, father_name, mother_name, father_no, mother_no, address,
                      user_login, user_pass, date_of_admission, date_of_joining, optional_subject
                    </code>. Leave <code style={{ fontFamily: 'monospace' }}>user_pass</code> blank to auto-generate a password.
                    Section is only required for classes that have sections.
                  </div>
                  <button
                    onClick={() => downloadTextFile('student_bulk_register_template.csv', buildTemplateCSV())}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 8,
                      padding: '9px 16px', borderRadius: 10, border: '1.5px solid #bfdbfe',
                      background: 'rgba(219,234,254,0.4)', color: '#1e40af',
                      fontSize: 13, fontWeight: 600, cursor: 'pointer',
                    }}
                  >
                    <i className="pi pi-download" style={{ fontSize: 12 }} /> Download CSV Template
                  </button>
                </Card>

                <Card>
                  <SectionHeader icon="pi pi-cloud-upload" title="Upload File" />

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv"
                    onChange={onFileInputChange}
                    style={{ display: 'none' }}
                  />

                  <div
                    className={`brc-dropzone${dragActive ? ' active' : ''}`}
                    onClick={() => fileInputRef.current?.click()}
                    onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
                    onDragLeave={() => setDragActive(false)}
                    onDrop={onDrop}
                    style={{
                      border: '2px dashed #cbd5e1', borderRadius: 14, padding: '28px 20px',
                      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
                      cursor: 'pointer', background: 'rgba(248,250,252,0.6)', textAlign: 'center',
                    }}
                  >
                    <div style={{
                      width: 44, height: 44, borderRadius: 12,
                      background: 'linear-gradient(135deg, rgba(59,130,246,0.12), rgba(59,130,246,0.06))',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <i className="pi pi-file" style={{ fontSize: 20, color: '#3b82f6' }} />
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#1f2937' }}>
                      {file ? file.name : 'Drop a CSV file here, or click to browse'}
                    </div>
                    <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 500 }}>.csv files only</div>
                  </div>

                  {parseError && (
                    <div style={{
                      marginTop: 12, display: 'flex', alignItems: 'center', gap: 8,
                      padding: '9px 13px', borderRadius: 8, background: '#fee2e2',
                      border: '1px solid #fca5a5', fontSize: 12, color: '#991b1b', fontWeight: 600,
                    }}>
                      <i className="pi pi-exclamation-triangle" /> {parseError}
                    </div>
                  )}

                  {uploadError && (
                    <div style={{
                      marginTop: 12, display: 'flex', alignItems: 'center', gap: 8,
                      padding: '9px 13px', borderRadius: 8, background: '#fee2e2',
                      border: '1px solid #fca5a5', fontSize: 12, color: '#991b1b', fontWeight: 600,
                    }}>
                      <i className="pi pi-exclamation-triangle" /> {uploadError}
                    </div>
                  )}

                  {previewRows && !parseError && (
                    <div style={{ marginTop: 14 }}>
                      <div style={{
                        display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10,
                        fontSize: 12, color: '#059669', fontWeight: 600,
                      }}>
                        <i className="pi pi-check-circle" />
                        {dataRowCount} student row{dataRowCount !== 1 ? 's' : ''} detected
                      </div>

                      {headerWarnings.length > 0 && (
                        <div style={{
                          marginBottom: 10, padding: '9px 13px', borderRadius: 8,
                          background: '#fef3c7', border: '1px solid #fcd34d',
                          fontSize: 12, color: '#92400e', fontWeight: 600,
                        }}>
                          <i className="pi pi-exclamation-triangle" style={{ marginRight: 6 }} />
                          Missing expected column{headerWarnings.length !== 1 ? 's' : ''}: {headerWarnings.join(', ')}.
                          The server will reject rows that are missing required fields.
                        </div>
                      )}

                      <div style={{ overflowX: 'auto', borderRadius: 10, border: '1px solid #e5e7eb' }}>
                        <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 11.5 }}>
                          <thead>
                            <tr>
                              {previewRows[0].map((h, i) => (
                                <th key={i} style={{
                                  textAlign: 'left', padding: '7px 10px', background: '#f9fafb',
                                  color: '#6b7280', fontWeight: 700, whiteSpace: 'nowrap',
                                  borderBottom: '1px solid #e5e7eb',
                                }}>{h.trim() || `col_${i + 1}`}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {previewRows.slice(1, 6).map((r, ri) => (
                              <tr key={ri}>
                                {r.map((cell, ci) => (
                                  <td key={ci} style={{
                                    padding: '7px 10px', whiteSpace: 'nowrap',
                                    borderBottom: '1px solid #f3f4f6', color: '#374151',
                                  }}>{cell || '—'}</td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {dataRowCount > 5 && (
                        <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 6, fontWeight: 500 }}>
                          Showing first 5 of {dataRowCount} rows.
                        </div>
                      )}
                    </div>
                  )}
                </Card>
              </>
            )}

            {stage === 'uploading' && (
              <Card>
                <SectionHeader icon="pi pi-sync" title="Uploading & Processing" />
                <div style={{ height: 8, borderRadius: 99, background: '#e5e7eb', overflow: 'hidden', marginBottom: 10 }}>
                  <div style={{
                    height: '100%', borderRadius: 99, width: `${progress}%`,
                    background: 'linear-gradient(90deg, #3b82f6, #60a5fa)', transition: 'width 0.3s ease',
                  }} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#6b7280', fontWeight: 500 }}>
                  <span>{progress < 100 ? 'Uploading file…' : 'Processing rows on the server…'}</span>
                  <span style={{ color: '#1e40af', fontWeight: 700 }}>{progress}%</span>
                </div>
              </Card>
            )}

            {stage === 'results' && response && (
              <>
                <Card>
                  <SectionHeader
                    icon={response.summary.failed === 0 ? 'pi pi-check-circle' : 'pi pi-exclamation-triangle'}
                    title="Import Summary"
                  />
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                    <div style={{ padding: '12px 10px', borderRadius: 10, background: '#f8fafc', border: '1px solid #e5e7eb', textAlign: 'center' }}>
                      <div style={{ fontSize: 20, fontWeight: 800, color: '#1f2937' }}>{response.summary.total_rows}</div>
                      <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 600, marginTop: 2 }}>Total Rows</div>
                    </div>
                    <div style={{ padding: '12px 10px', borderRadius: 10, background: 'rgba(220,252,231,0.6)', border: '1px solid #86efac', textAlign: 'center' }}>
                      <div style={{ fontSize: 20, fontWeight: 800, color: '#166534' }}>{response.summary.succeeded}</div>
                      <div style={{ fontSize: 11, color: '#166534', fontWeight: 600, marginTop: 2 }}>Succeeded</div>
                    </div>
                    <div style={{ padding: '12px 10px', borderRadius: 10, background: response.summary.failed > 0 ? 'rgba(254,226,226,0.7)' : '#f8fafc', border: `1px solid ${response.summary.failed > 0 ? '#fca5a5' : '#e5e7eb'}`, textAlign: 'center' }}>
                      <div style={{ fontSize: 20, fontWeight: 800, color: response.summary.failed > 0 ? '#991b1b' : '#9ca3af' }}>{response.summary.failed}</div>
                      <div style={{ fontSize: 11, color: response.summary.failed > 0 ? '#991b1b' : '#9ca3af', fontWeight: 600, marginTop: 2 }}>Failed</div>
                    </div>
                  </div>
                </Card>

                <Card>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, flexWrap: 'wrap', gap: 10 }}>
                    <SectionHeader icon="pi pi-list" title="Row-by-Row Results" />
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginBottom: 12, marginTop: -6 }}>
                    {(['all', 'success', 'error'] as ResultFilter[]).map((f) => {
                      const active = resultFilter === f;
                      const count = f === 'all' ? response.results.length : response.results.filter((r) => r.status === f).length;
                      return (
                        <button
                          key={f}
                          className="brc-filter-btn"
                          onClick={() => setResultFilter(f)}
                          style={{
                            padding: '6px 13px', borderRadius: 99, fontSize: 12, fontWeight: 700,
                            cursor: 'pointer', border: `1.5px solid ${active ? '#3b82f6' : '#e5e7eb'}`,
                            background: active ? '#3b82f6' : 'white',
                            color: active ? 'white' : '#6b7280',
                          }}
                        >
                          {f === 'all' ? 'All' : f === 'success' ? 'Succeeded' : 'Failed'} ({count})
                        </button>
                      );
                    })}
                    {response.summary.failed > 0 && (
                      <button
                        onClick={downloadErrorReport}
                        style={{
                          marginLeft: 'auto', padding: '6px 13px', borderRadius: 99, fontSize: 12, fontWeight: 700,
                          cursor: 'pointer', border: '1.5px solid #fca5a5', background: 'white', color: '#991b1b',
                          display: 'inline-flex', alignItems: 'center', gap: 6,
                        }}
                      >
                        <i className="pi pi-download" style={{ fontSize: 11 }} /> Error Report
                      </button>
                    )}
                  </div>

                  <div style={{ maxHeight: 280, overflowY: 'auto', borderRadius: 10, border: '1px solid #e5e7eb' }}>
                    {filteredResults.length === 0 && (
                      <div style={{ padding: '20px 12px', textAlign: 'center', fontSize: 12, color: '#9ca3af', fontWeight: 500 }}>
                        No rows in this filter.
                      </div>
                    )}
                    {filteredResults.map((r, i) => (
                      <div key={i} className="brc-result-row" style={{
                        display: 'flex', alignItems: 'flex-start', gap: 10,
                        padding: '10px 12px', borderBottom: '1px solid #f3f4f6',
                      }}>
                        <div style={{
                          width: 22, height: 22, borderRadius: '50%', flexShrink: 0, marginTop: 1,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          background: r.status === 'success' ? '#dcfce7' : '#fee2e2',
                          color: r.status === 'success' ? '#166534' : '#991b1b',
                        }}>
                          <i className={r.status === 'success' ? 'pi pi-check' : 'pi pi-times'} style={{ fontSize: 10 }} />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <span style={{ fontSize: 12, fontWeight: 700, color: '#1f2937' }}>Row {r.row}</span>
                            {r.student_name && <span style={{ fontSize: 12, color: '#6b7280', fontWeight: 500 }}>{r.student_name}</span>}
                            {r.status === 'success' && r.roll_no != null && (
                              <span style={{ fontSize: 10, padding: '1px 7px', borderRadius: 99, background: '#f1f5f9', color: '#475569', fontWeight: 600 }}>
                                {r.class_name}{r.section ? ` · ${r.section}` : ''} · Roll {r.roll_no}
                              </span>
                            )}
                          </div>
                          <div style={{ fontSize: 12, color: r.status === 'success' ? '#166534' : '#991b1b', fontWeight: 500, marginTop: 2 }}>
                            {r.message}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              </>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, borderTop: '1px solid #e5e7eb', paddingTop: 16, marginTop: 4 }}>
              <button
                className="brc-cancel-btn"
                style={{
                  padding: '9px 20px', borderRadius: 10, border: '1.5px solid #e5e7eb',
                  background: 'white', fontSize: 13, fontWeight: 600, color: '#6b7280',
                  cursor: stage === 'uploading' ? 'not-allowed' : 'pointer',
                }}
                onClick={handleClose}
                disabled={stage === 'uploading'}
              >
                {stage === 'results' ? 'Close' : 'Cancel'}
              </button>

              {stage === 'upload' && (
                <button
                  className="brc-submit-btn"
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 7,
                    padding: '9px 22px', borderRadius: 10, border: 'none',
                    background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
                    fontSize: 13, fontWeight: 700, color: 'white',
                    boxShadow: '0 4px 14px rgba(59,130,246,0.35)',
                    cursor: canSubmit ? 'pointer' : 'not-allowed',
                    opacity: canSubmit ? 1 : 0.6,
                  }}
                  onClick={handleUpload}
                  disabled={!canSubmit}
                >
                  <i className="pi pi-upload" style={{ fontSize: 13 }} />
                  Upload {dataRowCount > 0 ? `${dataRowCount} Student${dataRowCount !== 1 ? 's' : ''}` : 'CSV'}
                </button>
              )}

              {stage === 'results' && (
                <button
                  className="brc-submit-btn"
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 7,
                    padding: '9px 22px', borderRadius: 10, border: 'none',
                    background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
                    fontSize: 13, fontWeight: 700, color: 'white',
                    boxShadow: '0 4px 14px rgba(59,130,246,0.35)', cursor: 'pointer',
                  }}
                  onClick={reset}
                >
                  <i className="pi pi-replay" style={{ fontSize: 13 }} /> Upload Another File
                </button>
              )}
            </div>

          </div>
        </Dialog>
      </>
    );
  }
);

BulkRegisterStudentsCSV.displayName = 'BulkRegisterStudentsCSV';
export default BulkRegisterStudentsCSV;